import { useId, useMemo, useState, forwardRef, useCallback } from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { Layout } from "./layout.js"
import { classes } from "utils"
import { typography, colors } from "design"
import calendarIcon from "icons/calendar.svg"
import { Controller, useFormContext } from "react-hook-form"

const ReactDatePicker = DatePicker.default ?? DatePicker

export const datepickerVariablesLight = {
  "--resbird-datepicker-focus-border-color": colors.primaryBlueGrey,
  "--resbird-datepicker-border-color": colors.grey400,
  "--resbird-datepicker-color": colors.black,
  "--resbird-datepicker-color-error": colors.red,
  "--resbird-datepicker-label-color": colors.lightGrey100,
  "--resbird-datepicker-label-error": colors.red,
  "--resbird-datepicker-error-border-color": colors.red,
  "--resbird-datepicker-required-color": colors.red,
  "--resbird-datepicker-disabled-background": colors.grey200,
}

const computeDatepickerStyle = ({ error }) => ({
  height: 45,
  fontSize: 16,
  borderRadius: 4,
  color: "var(--resbird-datepicker-color)",
  outline: 0,
  border: "1px solid",
  boxSizing: "border-box",
  borderColor: error
    ? "var(--resbird-datepicker-error-border-color)"
    : "var(--resbird-datepicker-border-color)",
  ":focus-within": {
    border: "2px solid",
    borderColor: error
      ? "var(--resbird-datepicker-error-border-color)"
      : "var(--resbird-datepicker-focus-border-color)",
  },
  ":disabled": {
    backgroundColor: "var(--resbird-datepicker-disabled-background)",
    cursor: "not-allowed",
  },
  "&.datepicker": {
    width: "100%",
  },
  "&.react-datepicker-popper": {
    width: "400px",
  },
})

export const Datepicker = forwardRef(
  (
    {
      className,
      fieldName = "datepicker",
      label,
      labelStyle,
      error: propsError,
      errorStyle,
      rules = {},
      disabled,
      setToMidnight,
      isFormField = true,
      ...props
    },
    propsRef,
  ) => {
    const id = useId()
    const errorId = useId()
    const [dateValue, setDateValue] = useState(
      props.isRange ? [props.fromDate ?? null, props.toDate ?? null] : null,
    )

    const { control, formState } = useFormContext() ?? {}

    const error = useMemo(
      () =>
        !_.isEmpty(rules) || props.required
          ? propsError || _.get(formState.errors, fieldName)?.message
          : null,
      [fieldName, formState, props.required, propsError, rules],
    )

    const datepickerStyle = useMemo(
      () => computeDatepickerStyle({ error }),
      [error],
    )

    const handleChange = useCallback(
      (value) => {
        if (props.isRange) {
          setDateValue(value)
          props.onChange?.(value)
        } else {
          setDateValue(value)
          props.onChange?.(value)
        }
      },
      [props],
    )

    return isFormField ? (
      <Controller
        control={control}
        name={fieldName}
        ref={propsRef}
        rules={rules}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <Layout
            className={classes("datepicker-container", className)}
            css={{
              cursor: "pointer",
              // maxWidth: 407,
              ".react-datepicker-wrapper": { width: "100%" },
            }}
            role="select"
            ref={ref}
            vertical
          >
            {label && (
              <label
                className="select-label"
                css={[
                  {
                    marginBottom: 11,
                    color: "var(--resbird-datepicker-label-color)",
                  },
                  typography.widgetLabel,
                  labelStyle,
                ]}
                htmlFor={id}
              >
                <span>{`${label}:`}</span>
                {rules.required && (
                  <span
                    className="required-indicator"
                    css={{
                      color: "var(--resbird-datepicker-required-color)",
                    }}
                  >
                    *
                  </span>
                )}
              </label>
            )}
            <ReactDatePicker
              className="datepicker"
              css={datepickerStyle}
              showPopperArrow={false}
              popperClassName="datepicker-floating-ui-popper"
              popperPlacement="bottom-end"
              wrapperClassName="date-picker"
              dateFormat="dd-MMM-yyyy"
              icon={
                <img
                  src={calendarIcon}
                  className="calendar-icon"
                  alt="calendar-icon"
                  css={{ right: 0, top: "5%", width: 24, height: 24 }}
                />
              }
              aria-describedby={error ? errorId : undefined}
              toggleCalendarOnIconClick
              onKeyDown={(e) => e.preventDefault()}
              onChange={(value) => {
                if (!disabled && value) {
                  let newDate = new Date(value)

                  if (setToMidnight) {
                    newDate.setHours(0, 0, 0, 0) // Set to midnight in local time
                  }

                  onChange(newDate.getTime()) // Send timestamp to hook form
                }
              }}
              onBlur={onBlur} // notify when input is touched/blur
              selected={value}
              autoComplete="off"
              disabled={disabled}
              showIcon
              {...{ id, ...props }}
            />
            {error && (
              <label
                className="error-description"
                css={[
                  errorStyle,
                  {
                    marginTop: 5,
                    fontSize: 14,
                    color: "var(--resbird-datepicker-label-error)",
                  },
                ]}
                id={errorId}
                role="aria-errormessage"
              >
                {error}
              </label>
            )}
          </Layout>
        )}
      />
    ) : (
      <ReactDatePicker
        className="datepicker"
        css={datepickerStyle}
        showPopperArrow={false}
        popperClassName="datepicker-floating-ui-popper"
        popperPlacement="bottom-end"
        wrapperClassName="date-picker"
        dateFormat="dd-MMM-yyyy"
        selectsRange
        icon={
          <img
            src={calendarIcon}
            className="calendar-icon"
            alt="calendar-icon"
            css={{ right: 0, top: "5%", width: 24, height: 24 }}
          />
        }
        aria-describedby={error ? errorId : undefined}
        toggleCalendarOnIconClick
        onKeyDown={(e) => e.preventDefault()}
        autoComplete="off"
        disabled={disabled}
        showIcon
        {...{
          id,
          ...props,
          ...(props.isRange
            ? {
                startDate: dateValue?.[0],
                endDate: dateValue?.[1],
                onChange: handleChange,
              }
            : {}),
        }}
      />
    )
  },
)

Datepicker.displayName = "Datepicker"
